/**
 * DATA & PRIVACY (Phase 5 depth, FR-U-ID-012). A participant can view, correct, and request deletion of
 * their personal data, under the Kenya Data Protection Act. These are legal rights, not settings, so the
 * screen states them plainly and does not hide the consequential ones.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Dialog } from '@/components/overlay';

export default function DataPrivacy() {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [requested, setRequested] = useState(false);

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Link
        to="/settings"
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 6l-6 6 6 6" />
        </svg>
        Settings
      </Link>

      <header>
        <h1 className="text-page-title text-ink">Data & privacy</h1>
        <p className="mt-0.5 text-sm text-ink-muted">
          Your rights over your personal data, under the Data Protection Act.
        </p>
      </header>

      <section className="divide-y divide-border overflow-hidden rounded-card border border-border bg-surface shadow-card">
        <Item
          title="Download my data"
          body="Get a copy of everything we hold about you: your profile, verification, and activity."
          action={requested ? 'Requested' : 'Request'}
          onAction={() => setRequested(true)}
          done={requested}
        />
        <Item
          title="Correct my information"
          body="Something wrong on your profile? Ask us to fix it."
          action="Request a correction"
          onAction={() => setRequested(true)}
        />
        <Item
          title="What we keep, and for how long"
          body="Records tied to compliance and the audit trail are retained as the law requires, even after deletion."
          action="Read the retention policy"
          href="/disclosures"
        />
      </section>

      {/* The consequential one, kept plain and separate. */}
      <section className="rounded-card border border-danger/30 bg-danger/5 p-5">
        <h2 className="font-semibold text-ink">Delete my account</h2>
        <p className="mt-1 text-sm text-ink-muted">
          Closes your account and erases your personal data, except records the law requires us to
          keep. This cannot be undone, and any balance must be withdrawn first.
        </p>
        <button
          onClick={() => setConfirmDelete(true)}
          className="mt-4 rounded-field border border-danger/40 bg-surface px-4 py-2.5 text-sm font-medium text-danger transition hover:bg-danger/10"
        >
          Delete my account
        </button>
      </section>

      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete your account?"
      >
        <p className="mt-2 text-sm leading-relaxed text-ink-muted">
          This erases your personal data and cannot be undone. Compliance and audit records are
          retained as the law requires. Withdraw any balance before you continue.
        </p>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row-reverse">
          <button
            onClick={() => setConfirmDelete(false)}
            className="rounded-field bg-danger px-4 py-2.5 text-sm font-medium text-white transition hover:opacity-90"
          >
            Yes, delete my account
          </button>
          <button
            onClick={() => setConfirmDelete(false)}
            className="rounded-field border border-border px-4 py-2.5 text-sm font-medium text-ink transition hover:bg-surface-sunken"
          >
            Keep my account
          </button>
        </div>
      </Dialog>
    </div>
  );
}

function Item({
  title,
  body,
  action,
  onAction,
  href,
  done,
}: {
  title: string;
  body: string;
  action: string;
  onAction?: () => void;
  href?: string;
  done?: boolean;
}) {
  const inner = (
    <div className="px-4 py-4">
      <p className="text-sm font-medium text-ink">{title}</p>
      <p className="mt-0.5 text-sm text-ink-muted">{body}</p>
      <span
        className={`mt-2 inline-block text-sm font-medium ${done ? 'text-accent' : 'text-brand'}`}
      >
        {action}
      </span>
    </div>
  );
  if (href)
    return (
      <Link to={href} className="block transition hover:bg-surface-sunken">
        {inner}
      </Link>
    );
  return (
    <button
      onClick={onAction}
      className="block w-full text-left transition hover:bg-surface-sunken"
    >
      {inner}
    </button>
  );
}
